import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  MenuItem,
  Chip
} from '@mui/material';
import { Add, Edit, Delete, Pets } from '@mui/icons-material';
import { animauxAPI, clientsAPI } from '../../services/api';

const AnimauxPage = () => {
  const [animaux, setAnimaux] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAnimal, setCurrentAnimal] = useState(null);
  const [formData, setFormData] = useState({
    nom_animal: '',
    espece: '',
    race: '',
    age: '',
    antecedents: '',
    id_client: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [animauxResponse, clientsResponse] = await Promise.all([
        animauxAPI.getAll(),
        clientsAPI.getAll()
      ]);
      setAnimaux(animauxResponse.data.data || []);
      setClients(clientsResponse.data.data || []);
    } catch (error) {
      showSnackbar('Erreur lors du chargement des données', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (animal = null) => {
    if (animal) {
      setEditMode(true);
      setCurrentAnimal(animal);
      setFormData({
        nom_animal: animal.nom_animal || '',
        espece: animal.espece || '',
        race: animal.race || '',
        age: animal.age || '',
        antecedents: animal.antecedents || '',
        id_client: animal.id_client || ''
      });
    } else {
      setEditMode(false);
      setCurrentAnimal(null);
      setFormData({
        nom_animal: '',
        espece: '',
        race: '',
        age: '',
        antecedents: '',
        id_client: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentAnimal(null);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode && currentAnimal) {
        await animauxAPI.update(currentAnimal.id_animal, formData);
        showSnackbar('Animal modifié avec succès', 'success');
      } else {
        await animauxAPI.create(formData);
        showSnackbar('Animal créé avec succès', 'success');
      }
      handleCloseDialog();
      loadData();
    } catch (error) {
      showSnackbar('Erreur lors de la sauvegarde', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet animal ?')) {
      try {
        await animauxAPI.delete(id);
        showSnackbar('Animal supprimé avec succès', 'success');
        loadData();
      } catch (error) {
        showSnackbar('Erreur lors de la suppression', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getClientName = (idClient) => {
    const client = clients.find(c => c.id_client === idClient);
    return client ? `${client.nom} ${client.prenom}` : 'N/A';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <Pets sx={{ mr: 1, verticalAlign: 'middle' }} />
          Gestion des Animaux
        </Typography>
        <Button 
          variant="contained" 
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          disabled={clients.length === 0}
        >
          Nouvel Animal
        </Button>
      </Box>

      {clients.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Vous devez d'abord créer des clients avant d'ajouter des animaux.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>ID</strong></TableCell>
              <TableCell><strong>Nom</strong></TableCell>
              <TableCell><strong>Espèce</strong></TableCell>
              <TableCell><strong>Race</strong></TableCell>
              <TableCell><strong>Âge</strong></TableCell>
              <TableCell><strong>Propriétaire</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {animaux.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="text.secondary">
                    Aucun animal trouvé. Cliquez sur "Nouvel Animal" pour en ajouter un.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              animaux.map((animal) => (
                <TableRow key={animal.id_animal} hover>
                  <TableCell>{animal.id_animal}</TableCell>
                  <TableCell>
                    <strong>{animal.nom_animal}</strong>
                  </TableCell>
                  <TableCell>
                    <Chip label={animal.espece} color="primary" size="small" />
                  </TableCell>
                  <TableCell>{animal.race}</TableCell>
                  <TableCell>{animal.age}</TableCell>
                  <TableCell>{getClientName(animal.id_client)}</TableCell>
                  <TableCell align="center">
                    <IconButton 
                      color="primary" 
                      size="small"
                      onClick={() => handleOpenDialog(animal)}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton 
                      color="error" 
                      size="small"
                      onClick={() => handleDelete(animal.id_animal)}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog d'ajout/modification */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode ? 'Modifier l\'animal' : 'Nouvel animal'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              name="nom_animal"
              label="Nom de l'animal"
              fullWidth
              margin="normal"
              value={formData.nom_animal}
              onChange={handleChange}
              required
            />
            <TextField
              name="espece"
              label="Espèce"
              fullWidth
              margin="normal"
              value={formData.espece}
              onChange={handleChange}
              required
              select
            >
              <MenuItem value="Chien">Chien</MenuItem>
              <MenuItem value="Chat">Chat</MenuItem>
              <MenuItem value="Lapin">Lapin</MenuItem>
              <MenuItem value="Oiseau">Oiseau</MenuItem>
              <MenuItem value="Reptile">Reptile</MenuItem>
              <MenuItem value="Autre">Autre</MenuItem>
            </TextField>
            <TextField
              name="race"
              label="Race"
              fullWidth
              margin="normal"
              value={formData.race}
              onChange={handleChange}
            />
            <TextField
              name="age"
              label="Âge"
              fullWidth
              margin="normal"
              value={formData.age}
              onChange={handleChange}
              placeholder="Ex: 3 ans, 6 mois..."
            />
            <TextField
              name="id_client"
              label="Propriétaire"
              fullWidth
              margin="normal"
              value={formData.id_client}
              onChange={handleChange}
              required
              select
            >
              {clients.map((client) => (
                <MenuItem key={client.id_client} value={client.id_client}>
                  {client.nom} {client.prenom}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              name="antecedents"
              label="Antécédents médicaux"
              fullWidth
              margin="normal"
              multiline
              rows={3}
              value={formData.antecedents}
              onChange={handleChange}
              placeholder="Maladies, allergies, opérations..."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button type="submit" variant="contained">
              {editMode ? 'Modifier' : 'Créer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AnimauxPage;